export type ToastTone = 'info' | 'warn' | 'error' | 'success'

export type ToastAction = {
  label: string
  run: () => void | Promise<void>
}

export type ToastInput = {
  tone?: ToastTone
  message: string
  timeoutMs?: number | null
  persistent?: boolean
  action?: ToastAction
}

export type ToastMessage = {
  id: number
  tone: ToastTone
  message: string
  timeoutMs: number | null
  action?: ToastAction
}

const MAX_TOASTS = 5

function defaultTimeoutMs(tone: ToastTone) {
  if (tone === 'success' || tone === 'info') return 3000
  if (tone === 'warn') return 5500
  return 8000
}

export const useToastsStore = defineStore('toasts', () => {
  const messages = ref<ToastMessage[]>([])
  let nextId = 0
  const timers = new Map<number, ReturnType<typeof setTimeout>>()

  function clearTimer(id: number) {
    const timer = timers.get(id)
    if (!timer) return
    clearTimeout(timer)
    timers.delete(id)
  }

  function dismiss(id: number) {
    clearTimer(id)
    messages.value = messages.value.filter((toast) => toast.id !== id)
  }

  function push(input: ToastInput) {
    const tone = input.tone ?? 'info'
    const id = ++nextId
    const timeoutMs = input.persistent || input.action ? null : input.timeoutMs ?? defaultTimeoutMs(tone)
    const toast: ToastMessage = {
      id,
      tone,
      message: input.message,
      timeoutMs,
      action: input.action,
    }
    const nextMessages = [toast, ...messages.value].slice(0, MAX_TOASTS)
    for (const removed of messages.value) {
      if (!nextMessages.some((nextToast) => nextToast.id === removed.id)) clearTimer(removed.id)
    }
    messages.value = nextMessages
    if (timeoutMs && timeoutMs > 0) {
      timers.set(id, setTimeout(() => dismiss(id), timeoutMs))
    }
    return id
  }

  async function runAction(id: number) {
    const toast = messages.value.find((message) => message.id === id)
    if (!toast?.action) return
    try {
      await toast.action.run()
      dismiss(id)
    } catch (err: any) {
      push({ tone: 'error', message: String(err?.message ?? err) })
    }
  }

  function clear() {
    for (const id of timers.keys()) clearTimer(id)
    messages.value = []
  }

  return {
    messages,
    push,
    dismiss,
    runAction,
    clear,
  }
})
